import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Crop, FileImage, Loader2, RotateCcw, RotateCw, Scan } from 'lucide-react';
import { fullFrame, type Point, type Quad } from '../../lib/scan/geometry.js';
import { decodePhoto, type Photo } from '../../lib/scan/pipeline.js';
import { renderPage, type ScanFilter } from '../../lib/scan/render.js';
import { Button } from '../ui.js';
import { useContainRect } from './useContainRect.js';

/**
 * SCAN-006 — one page, looked at and put right.
 *
 * Two views of the same page, and the order of them is the argument. First the
 * scan itself — flattened, cleaned up, turned the way it will be turned in the
 * file — because that is the thing being judged and the only thing anybody
 * asked to see. Behind it, the photograph with the four corners on it, for when
 * the answer to "is this right?" is no.
 *
 * It used to open on the corners, and rotating was a button that changed a
 * number: the photograph underneath stayed exactly where it was, the outline
 * stayed exactly where it was, and the only place the turn showed up was a
 * hundred-pixel thumbnail two screens later. Reported, reasonably, as rotation
 * not working at all.
 *
 * Nothing else in this product drags. Reordering is two buttons, placing a
 * signature is a tap, and the reason is written where those are: a drag is a
 * gesture that fails on a phone more often than it works, and a failed drag
 * leaves the person unsure whether they missed or the application did.
 *
 * A crop corner is the exception, and it is one on its own terms rather than by
 * being an interesting case. The alternatives were tried in the design of every
 * scanner that exists: nudging a corner with arrows is four controls and a
 * dozen taps per corner; re-photographing until the automatic guess is right is
 * what people already do and is what this whole screen exists to stop. And the
 * objections do not apply here — the target is a still image that nothing
 * scrolls, the handle is a 44-pixel circle rather than a thumbnail in a list,
 * and a drag that goes wrong is undone by dragging back.
 *
 * The magnifier is part of the same argument: the one thing a finger on a crop
 * corner cannot do is see the corner, because the finger is on top of it.
 */

/** Twice this, as a square, is the touch target. Comfortably past the 44px a
 *  fingertip needs, because the handle sits at the very edge of the picture. */
const HANDLE = 26;

/** The magnifier's window, in screen pixels, and how much it enlarges. */
const LENS = 92;
const LENS_ZOOM = 2.6;

/** How large the preview is rendered. Twice the box it is shown in on a phone
 *  with a doubled screen, and a fifth of the work of rendering it at the size
 *  the page goes into the file at — which this is redone at on every turn. */
const PREVIEW_MAX = 1100;

type View = 'page' | 'crop';

export function PageEditor({
  url,
  photo,
  quad,
  rotation,
  filter,
  onApply,
  onCancel,
}: {
  /** The photograph, as an object URL — shown, and magnified, from the same one. */
  url: string;
  photo: Photo;
  quad: Quad;
  rotation: number;
  /** The look chosen for every page, so the preview is of the actual page. */
  filter: ScanFilter;
  onApply: (next: { quad: Quad; rotation: number }) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const picture = useContainRect(box, photo);

  const [view, setView] = useState<View>('page');
  const [corners, setCorners] = useState<Quad>(quad);
  const [turns, setTurns] = useState(rotation);
  const [dragging, setDragging] = useState<number | null>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [rendering, setRendering] = useState(true);
  const [broken, setBroken] = useState(false);
  /* The decoded photograph, kept for as long as this screen is open: every
     turn and every corner moved re-renders from it, and decoding a two-and-a-
     half thousand pixel JPEG each time is the one part of that worth avoiding. */
  const sourceRef = useRef<ImageBitmap | HTMLImageElement | null>(null);
  const previewRef = useRef<string | null>(null);

  /*
   * The scan, as it currently stands.
   *
   * Redone whenever the corners or the turn change, which is to say whenever
   * the answer would be different — this is the picture the whole screen is
   * about, and a preview that lags behind the button that changed it is the
   * same bug as not rotating at all, only harder to describe.
   */
  useEffect(() => {
    let cancelled = false;
    setRendering(true);
    void (async () => {
      try {
        if (!sourceRef.current) sourceRef.current = await decodePhoto(photo.blob);
        const { blob } = await renderPage(sourceRef.current, {
          quad: corners,
          rotation: turns,
          filter,
          maxSize: PREVIEW_MAX,
        });
        if (cancelled) return;
        const next = URL.createObjectURL(blob);
        if (previewRef.current) URL.revokeObjectURL(previewRef.current);
        previewRef.current = next;
        setPreview(next);
        setBroken(false);
      } catch {
        // Two corners dragged onto each other, most of the time: there is no
        // page to flatten and the crop is what has to change.
        if (!cancelled) setBroken(true);
      } finally {
        if (!cancelled) setRendering(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [corners, turns, filter, photo]);

  useEffect(
    () => () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
      const source = sourceRef.current;
      if (source instanceof ImageBitmap) source.close();
    },
    [],
  );

  /** A point on the screen, as a pixel of the photograph. */
  const toPhoto = (clientX: number, clientY: number): Point | null => {
    const bounds = box?.getBoundingClientRect();
    if (!bounds || !picture) return null;
    const scale = photo.width / picture.width;
    return {
      x: Math.max(0, Math.min(photo.width, (clientX - bounds.left - picture.left) * scale)),
      y: Math.max(0, Math.min(photo.height, (clientY - bounds.top - picture.top) * scale)),
    };
  };

  /** A pixel of the photograph, as a point inside the box on screen. */
  const toScreen = (point: Point): Point => {
    if (!picture) return { x: 0, y: 0 };
    const scale = picture.width / photo.width;
    return { x: picture.left + point.x * scale, y: picture.top + point.y * scale };
  };

  const move = (index: number, clientX: number, clientY: number) => {
    const moved = toPhoto(clientX, clientY);
    if (!moved) return;
    setCorners((current) => current.map((p, i) => (i === index ? moved : p)) as unknown as Quad);
  };

  const held = dragging === null ? null : toScreen(corners[dragging] as Point);

  const tab = (name: View, label: string, icon: ReactNode) => (
    <button
      type="button"
      onClick={() => setView(name)}
      aria-pressed={view === name}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${
        view === name
          ? 'border-primary bg-primary/10 text-primary font-medium'
          : 'border-border text-muted-foreground hover:bg-muted'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {tab('page', t('scan.viewPage'), <FileImage className="h-4 w-4" aria-hidden />)}
        {tab('crop', t('scan.viewCrop'), <Crop className="h-4 w-4" aria-hidden />)}
        {rendering && (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground ms-auto">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            {t('scan.preparing')}
          </span>
        )}
      </div>

      {view === 'page' ? (
        <div className="relative w-full h-[46vh] min-h-[220px] rounded-xl overflow-hidden bg-muted flex items-center justify-center">
          {preview && !broken ? (
            // The finished page, whole rather than filled to the box: a scan
            // shown cropped to a tile is a scan whose crop cannot be checked.
            <img src={preview} alt="" className="w-full h-full object-contain" />
          ) : (
            <p className="px-6 text-center text-sm text-muted-foreground">
              {broken ? t('scan.cropBroken') : t('scan.preparing')}
            </p>
          )}
        </div>
      ) : (
        <div
          ref={setBox}
          className="relative w-full h-[46vh] min-h-[220px] rounded-xl overflow-hidden bg-foreground select-none touch-none"
        >
          <img src={url} alt="" className="absolute inset-0 w-full h-full object-contain" draggable={false} />

          {picture && (
            <>
              <svg
                className="absolute pointer-events-none"
                style={{ left: picture.left, top: picture.top, width: picture.width, height: picture.height }}
                viewBox={`0 0 ${photo.width} ${photo.height}`}
                preserveAspectRatio="none"
                aria-hidden
              >
                <path
                  d={`M0 0H${photo.width}V${photo.height}H0Z M${corners.map((p) => `${p.x} ${p.y}`).join('L')}Z`}
                  fillRule="evenodd"
                  className="fill-foreground/55"
                />
                <polygon
                  points={corners.map((p) => `${p.x},${p.y}`).join(' ')}
                  className="fill-none stroke-primary"
                  strokeWidth={Math.max(2, photo.width / 300)}
                  strokeLinejoin="round"
                />
              </svg>

              {corners.map((corner, index) => {
                const at = toScreen(corner);
                return (
                  <button
                    key={index}
                    type="button"
                    aria-label={t(`scan.corner.${index}`)}
                    className="absolute rounded-full border-2 border-primary bg-background/85 touch-none"
                    style={{ left: at.x - HANDLE, top: at.y - HANDLE, width: HANDLE * 2, height: HANDLE * 2 }}
                    onPointerDown={(event) => {
                      // Capture, so the corner keeps following the finger once it
                      // has slid off the 52px circle — which it does immediately,
                      // because the interesting corners are at the edge of the
                      // screen and the finger is bigger than the handle.
                      event.currentTarget.setPointerCapture(event.pointerId);
                      setDragging(index);
                    }}
                    onPointerMove={(event) => {
                      if (dragging !== index) return;
                      move(index, event.clientX, event.clientY);
                    }}
                    onPointerUp={() => setDragging(null)}
                    onPointerCancel={() => setDragging(null)}
                    onKeyDown={(event) => {
                      // The same corner, for a keyboard. A crop is not a thing
                      // that may only be done with a finger.
                      const step = event.shiftKey ? 20 : 4;
                      const shift: Record<string, [number, number]> = {
                        ArrowLeft: [-step, 0],
                        ArrowRight: [step, 0],
                        ArrowUp: [0, -step],
                        ArrowDown: [0, step],
                      };
                      const by = shift[event.key];
                      if (!by) return;
                      event.preventDefault();
                      setCorners(
                        (current) =>
                          current.map((p, i) =>
                            i === index
                              ? {
                                  x: Math.max(0, Math.min(photo.width, p.x + by[0])),
                                  y: Math.max(0, Math.min(photo.height, p.y + by[1])),
                                }
                              : p,
                          ) as unknown as Quad,
                      );
                    }}
                  />
                );
              })}

              {/*
                The magnifier, shown only while a corner is held and always in the
                far corner from the finger, so it is never the thing being covered.
              */}
              {held && (
                <div
                  className="absolute rounded-xl border border-border overflow-hidden shadow-lg pointer-events-none"
                  style={{
                    width: LENS,
                    height: LENS,
                    left: held.x < (picture.width + picture.left * 2) / 2 ? undefined : 8,
                    right: held.x < (picture.width + picture.left * 2) / 2 ? 8 : undefined,
                    top: 8,
                    backgroundImage: `url(${url})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: `${picture.width * LENS_ZOOM}px ${picture.height * LENS_ZOOM}px`,
                    backgroundPosition: `${LENS / 2 - (held.x - picture.left) * LENS_ZOOM}px ${
                      LENS / 2 - (held.y - picture.top) * LENS_ZOOM
                    }px`,
                  }}
                >
                  <span className="absolute inset-x-0 top-1/2 h-px bg-primary/70" />
                  <span className="absolute inset-y-0 left-1/2 w-px bg-primary/70" />
                </div>
              )}
            </>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        {view === 'page' ? t('scan.previewHint') : t('scan.cropHint')}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {/* Turning is offered where the turn can be seen. On the crop it is a
            button that changes a number and nothing on the screen. */}
        {view === 'page' ? (
          <>
            <Button
              type="button"
              variant="ghost"
              icon={<RotateCcw className="h-4 w-4" aria-hidden />}
              onClick={() => setTurns((current) => (current + 3) % 4)}
            >
              {t('scan.rotateLeft')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              icon={<RotateCw className="h-4 w-4" aria-hidden />}
              onClick={() => setTurns((current) => (current + 1) % 4)}
            >
              {t('scan.rotateRight')}
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant="ghost"
            icon={<Scan className="h-4 w-4" aria-hidden />}
            onClick={() => setCorners(fullFrame(photo.width, photo.height))}
          >
            {t('scan.wholePhoto')}
          </Button>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="button" variant="primary" onClick={() => onApply({ quad: corners, rotation: turns })}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
